import { XCircleIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import cn from 'classnames';
import { differenceInYears, parseISO } from 'date-fns';
import isEmpty from 'lodash.isempty';
import PropTypes from 'prop-types';
import { useApplicationStore } from './applicationStore';
import countries from './countries.json';
import states from './states.json';

const ClientForm = ({ onlyProfile = false }) => {
	const {
		firstName: nameFirst,
		setNameFirst,
		middleName: nameMiddle,
		setNameMiddle,
		lastName: nameLast,
		setNameLast,
		nickname,
		setNickname,
		email,
		setEmail,
		mobile,
		setMobile,
		dob,
		setDob,
		address,
		setAddress,
		city,
		setCity,
		state,
		setState,
		zip,
		setZip,
		maritalStatus,
		setMaritalStatus,
		disabilityStatus,
		setDisibilityStatus,
		veteranStatus,
		setVeteranStatus,
		ethnicity,
		setEthnicity,
		race,
		setRace,
		// healthplan,
		// setHealthplan,
		// subscriberId,
		// setSubscriberId,
		spokenLanguage,
		setSpokenLanguage,
		writtenLanguage,
		setWrittenLanguage,
		homeless,
		setHomeless,
		mothersName,
		setMothersName,
		fathersName,
		setFathersName,
		countryOfBirth,
		setCountryOfBirth,
		genderIdent,
		setGenderIdent,
		insuranceType,
		setInsuranceType,
		medicaidPlan,
		setMedicaidPlan,
	} = useApplicationStore();

	const emailQuery = useQuery({
		queryKey: ['email', email],
		queryFn: async () => {
			const { data } = await axios.get('/requests/check-email', {
				params: { email },
			});
			return data;
		},
		enabled: false,
	});

	// const healthPlanQuery = useQuery({
	// 	queryKey: ['healthplan-agencies'],
	// 	queryFn: async () => {
	// 		const { data } = await axios.get('/requests/get-healthplan-agencies');
	// 		return data;
	// 	},
	// });

	const handleCheckEmail = () => {
		if (!isEmpty(email)) {
			emailQuery.refetch();
		}
	};

	// Helper to check if under 18
	const isUnder18 = (() => {
		if (!dob) return false;
		try {
			const age = differenceInYears(new Date(), parseISO(dob));
			return age < 18;
		} catch {
			return false;
		}
	})();

	return (
		<>
			<div className="form-control sm:col-span-2">
				<label htmlFor="first-name" className="label">
					<span className="label-text">
						First name <span className="text-error">*</span>
					</span>
				</label>
				<input
					type="text"
					name="first-name"
					id="first-name"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setNameFirst(target.value)}
					value={nameFirst}
					required
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="nickname" className="label">
					<span className="label-text">Nickname</span>
				</label>
				<input
					type="text"
					name="nickname"
					id="nickname"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setNickname(target.value)}
					value={nickname}
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="middle-name" className="label">
					<span className="label-text">Middle name</span>
				</label>
				<input
					type="text"
					name="middle-name"
					id="middle-name"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setNameMiddle(target.value)}
					value={nameMiddle}
				/>
			</div>
			<div className="form-control sm:col-span-3">
				<label htmlFor="last-name" className="label">
					<span className="label-text">
						Last name <span className="text-error">*</span>
					</span>
				</label>
				<input
					type="text"
					name="last-name"
					id="last-name"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setNameLast(target.value)}
					value={nameLast}
					required
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="mobile" className="label">
					<span className="label-text">
						Mobile Number <span className="text-error">*</span>
					</span>
				</label>
				<input
					id="mobile"
					name="mobile"
					type="tel"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setMobile(target.value)}
					value={mobile}
					required
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="dob" className="label">
					<span className="label-text">
						Date of Birth <span className="text-error">*</span>
					</span>
				</label>
				<input
					id="dob"
					name="dob"
					type="date"
					autoComplete="off"
					className="input input-bordered"
					onChange={({ target }) => setDob(target.value)}
					value={dob}
					required
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="insurance-type" className="label label-text">
					Insurance Type
				</label>
				<select
					id="insurance-type"
					className="select select-bordered"
					value={insuranceType}
					onChange={({ target }) => setInsuranceType(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="medicaid">Medicaid</option>
					<option value="medicare">Medicare</option>
					<option value="dual">Dual Eligible (Medicaid & Medicare)</option>
					<option value="private">Private</option>
					<option value="uninsured">Uninsured</option>
					<option value="other">Other</option>
					<option value="unknown">Unknown</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="medicaid-plan" className="label label-text">
					Plan Name
				</label>
				<select
					id="medicaid-plan"
					className="select select-bordered"
					disabled={insuranceType !== 'medicaid' && insuranceType !== 'dual'}
					value={medicaidPlan}
					onChange={({ target }) => setMedicaidPlan(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option>Blue Cross Complete</option>
					<option>Meridian</option>
					<option>Molina</option>
					<option>Priority Health</option>
					<option>United Health</option>
					<option>HAP</option>
					<option>Upper Peninsula Health Plan</option>
					<option>Other</option>
				</select>
			</div>
			{isUnder18 && (
				<>
					<div className="form-control sm:col-span-2">
						<label htmlFor="name-mothers" className="label">
							<span className="label-text">
								Mother&apos;s full maiden name{' '}
								<span className="text-error">*</span>
							</span>
						</label>
						<input
							id="name-mothers"
							name="name-mothers"
							type="text"
							autoComplete="off"
							className="input input-bordered"
							onChange={({ target }) => setMothersName(target.value)}
							value={mothersName}
							required
						/>
					</div>
					<div className="form-control sm:col-span-2">
						<label htmlFor="name-fathers" className="label">
							<span className="label-text">
								Father&apos;s full name <span className="text-error">*</span>
							</span>
						</label>
						<input
							id="name-fathers"
							name="name-fathers"
							type="text"
							autoComplete="off"
							className="input input-bordered"
							onChange={({ target }) => setFathersName(target.value)}
							value={fathersName}
							required
						/>
					</div>
				</>
			)}
			<div className="form-control sm:col-span-3">
				<label htmlFor="address" className="label label-text">
					Address
				</label>
				<input
					id="address"
					name="address"
					type="text"
					autoComplete="off"
					className="input input-bordered"
					value={address}
					onChange={({ target }) => setAddress(target.value)}
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="city" className="label label-text">
					City
				</label>
				<input
					id="city"
					name="city"
					type="text"
					autoComplete="off"
					className="input input-bordered"
					value={city}
					onChange={({ target }) => setCity(target.value)}
				/>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="state" className="label label-text">
					State
				</label>
				<select
					id="state"
					name="state"
					className="select select-bordered"
					autoComplete="off"
					value={state}
					onChange={({ target }) => setState(target.value)}
				>
					<option value="">Choose...</option>
					{states.map((state) => (
						<option key={state.abbreviation} value={state.abbreviation}>
							{state.name}
						</option>
					))}
				</select>
			</div>
			<div className="form-control sm:col-span-1">
				<label htmlFor="zip" className="label label-text">
					Zip
				</label>
				<input
					id="zip"
					name="zip"
					type="text"
					autoComplete="off"
					className="input input-bordered"
					value={zip}
					pattern="[0-9]{5}"
					onChange={({ target }) => setZip(target.value)}
				/>
			</div>
			<div className="form-control sm:col-span-3">
				<label htmlFor="email" className="label label-text">
					Email address
				</label>
				<input
					id="email"
					name="email"
					type="email"
					autoComplete="off"
					className={cn('input', 'input-bordered', {
						'input-error': emailQuery.data?.exists,
					})}
					onChange={({ target }) => setEmail(target.value)}
					value={email}
					readOnly={onlyProfile}
					onBlur={handleCheckEmail}
				/>
				{emailQuery.isFetched && (
					<>
						{emailQuery.data.exists && (
							<div className="label text-error">
								<span>
									<XCircleIcon className="inline-block w-4 h-4 text-error" /> An
									account already exists with this email address
								</span>
							</div>
						)}
					</>
				)}
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="country-of-birth" className="label label-text">
					Country of Birth
				</label>
				<select
					id="country-of-birth"
					className="select select-bordered"
					value={countryOfBirth}
					onChange={({ target }) => setCountryOfBirth(target.value)}
					autoComplete="off"
				>
					<option value="">Choose...</option>
					{countries.map((country) => (
						<option key={country.code} value={country.code}>
							{country.name}
						</option>
					))}
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="gender" className="label label-text">
					Gender Identity
				</label>
				<select
					id="gender"
					className="select select-bordered"
					value={genderIdent}
					onChange={({ target }) => setGenderIdent(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="Male">Male</option>
					<option value="Female">Female</option>
					{/* <option value="Transgender Male">Transgender Male</option>
					<option value="Transgender Female">Transgender Female</option>
					<option value="Genderqueer">Genderqueer</option>
					<option value="Non-binary">Non-binary</option> */}
					<option value="Other">
						Other (another gender identity not listed)
					</option>
					<option value="Choose not to disclose">Choose not to disclose</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="marital-status" className="label label-text">
					Marital Status
				</label>
				<select
					id="marital-status"
					className="select select-bordered"
					value={maritalStatus}
					onChange={({ target }) => setMaritalStatus(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="single">Single</option>
					<option value="married">Married</option>
					<option value="divorced">Divorced</option>
					<option value="widowed">Widowed</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="disability-status" className="label label-text">
					Disability
				</label>
				<select
					id="disability-status"
					className="select select-bordered"
					value={disabilityStatus}
					onChange={({ target }) => setDisibilityStatus(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="no">No</option>
					<option value="yes">Yes</option>
					<option value="decline">Decline to answer</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="veteran-status" className="label label-text">
					Veteran
				</label>
				<select
					id="veteran-status"
					className="select select-bordered"
					value={veteranStatus}
					onChange={({ target }) => setVeteranStatus(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="no">No</option>
					<option value="yes">Yes</option>
					<option value="decline">Decline to answer</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="ethnicity" className="label label-text">
					Ethnicity
				</label>
				<select
					id="ethnicity"
					className="select select-bordered"
					value={ethnicity}
					onChange={({ target }) => setEthnicity(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="yes">Hispanic or Latino</option>
					<option value="no">Not Hispanic or Latino</option>
					<option value="decline">Decline to answer</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="race" className="label label-text">
					Race
				</label>
				<select
					id="race"
					className="select select-bordered"
					value={race}
					onChange={({ target }) => setRace(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="amer_indian">American Indian or Alaska Native</option>
					<option value="asian">Asian</option>
					<option value="black">Black or African American</option>
					<option value="native_hawaiian_islander">
						Native Hawaiian or Other Pacific Islander
					</option>
					<option value="white">White</option>
					<option value="other">Other race</option>
					<option value="decline">Decline to answer</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="lang-spoken" className="label label-text">
					Spoken Language Preference
				</label>
				<select
					id="lang-spoken"
					className="select select-bordered"
					value={spokenLanguage}
					onChange={({ target }) => setSpokenLanguage(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="en">English</option>
					<option value="es">Spanish</option>
					<option value="ar">Arabic</option>
					<option value="other">Other</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="lang-written" className="label label-text">
					Written Language Preference
				</label>
				<select
					id="lang-written"
					className="select select-bordered"
					value={writtenLanguage}
					onChange={({ target }) => setWrittenLanguage(target.value)}
					autoComplete="off"
				>
					<option value=""></option>
					<option value="en">English</option>
					<option value="es">Spanish</option>
					<option value="ar">Arabic</option>
					<option value="other">Other</option>
				</select>
			</div>
			<div className="form-control sm:col-span-2">
				<label htmlFor="homeless" className="label label-text">
					Homeless
				</label>
				<select
					id="homeless"
					className="select select-bordered"
					value={homeless ? '1' : '0'}
					onChange={({ target }) => setHomeless(target.value === '1')}
					autoComplete="off"
				>
					<option value="0">No</option>
					<option value="1">Yes</option>
				</select>
			</div>
			{/* {healthPlanQuery.isFetched && healthPlanQuery?.data?.length > 0 && (
				<>
					<div className="form-control sm:col-span-2">
						<label htmlFor="healthplan" className="label label-text">
							Health Plan
						</label>
						<select
							id="healthplan"
							className="select select-bordered"
							value={healthplan}
							onChange={({ target }) => setHealthplan(target.value)}
							autoComplete="off"
						>
							<option value=""></option>
							{healthPlanQuery.data.map(({ id, agency_name }) => (
								<option key={`${id}`} value={id}>
									{agency_name}
								</option>
							))}
						</select>
					</div>
					<div className="form-control sm:col-span-2">
						<label htmlFor="subscriber-id" className="label label-text">
							Health Plan Subscriber ID
						</label>
						<input
							id="subscriber-id"
							name="subscriberId"
							type="text"
							autoComplete="none"
							className="input input-bordered"
							value={subscriberId}
							onChange={({ target }) => setSubscriberId(target.value)}
						/>
					</div>
				</>
			)} */}
		</>
	);
};

ClientForm.propTypes = {
	onlyProfile: PropTypes.bool,
};

export default ClientForm;
